import { useEffect, useMemo, useRef } from 'react';
import { isBrowser } from '~/utils';

interface GObserverProps {
  children?: React.ReactNode;
  targetId?: string;
  options?: IntersectionObserverInit;
  setIsIntersecting: (isIntersecting: boolean) => void;
}

export const GObserver = ({ children, targetId, options, setIsIntersecting }: GObserverProps) => {
  const observerRef = useRef<HTMLDivElement>(null);

  const { root, rootMargin, threshold } = options ?? {};

  const observerOptions = useMemo(() => {
    if (!isBrowser()) return;

    const target = targetId ? document.getElementById(targetId) : undefined;

    return {
      root: root ?? target ?? null,
      rootMargin: rootMargin ?? '0px',
      threshold: threshold ?? 1.0,
    };
  }, [root, rootMargin, targetId, threshold]);

  useEffect(() => {
    if (!observerRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsIntersecting(entry.isIntersecting);
      });
    }, observerOptions);

    observer.observe(observerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [observerOptions, setIsIntersecting]);

  return <div ref={observerRef}>{children}</div>;
};
